<template>
  <div class="alpha-bank-widget">
    <div class="alpha-bank-widget__container">
      <AlphaBankHeader
        :order-id="orderId ?? undefined"
        :amount-formatted="amountFormatted"
      />

      <AlphaBankError
        v-if="paymentError"
        :error="paymentError"
      />

      <template v-if="confirmationUrl">
        <!-- Веб-версия: платёжная страница банка во фрейме, прямо в модалке -->
        <template v-if="useFrame">
          <AlphaBankLoader v-if="isFrameLoading" />

          <AlphaBankPaymentFrame
            :confirmation-url="confirmationUrl"
            @load="handleFrameLoad"
          />
        </template>

        <!-- Мобильное приложение: банк требует системный браузер, не WebView-фрейм -->
        <template v-else>
          <p class="alpha-bank-widget__hint">
            Оплата откроется в защищённом окне Альфа-Банка. Данные карты вводятся на стороне
            банка — мы их не получаем.
          </p>

          <!-- Alfa Pay: нативная кнопка SDK, уводит прямо в приложение банка -->
          <button
            v-if="isAlfaPayReady"
            type="button"
            class="alpha-bank-widget__pay alpha-bank-widget__pay--alfapay"
            @click="openAlfaPay"
          >
            Оплатить через Alfa Pay
          </button>

          <button
            type="button"
            class="alpha-bank-widget__pay"
            :class="{ 'alpha-bank-widget__pay--secondary': isAlfaPayReady }"
            @click="openPaymentPage"
          >
            {{ isAlfaPayReady ? 'Другой способ оплаты' : 'Перейти к оплате' }}
          </button>
        </template>

        <p v-if="isTest" class="alpha-bank-widget__test">
          Тестовый режим Альфа-Банка — реальные деньги не списываются
        </p>

        <div class="alpha-bank-widget__status">
          <span>{{ statusText }}</span>
        </div>

        <AlphaBankFallback :confirmation-url="confirmationUrl" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import { consola } from 'consola'

import { usePaymentStore, usePaymentReconciliation } from '@features/payment'
import { apiAlfaBank } from '@features/payment/api'
import type { AlfaPayNativeConfigResult } from '@features/payment/api'
import { AlfaPayNative, isAlfaPayNativeAvailable } from '@features/payment/lib'
import { useModalStore, modalStateEnum } from '@shared/model'
import { useUserProfileStore } from '@entities/user'
import { isNativePlatform, openExternalUrl } from '@shared/lib'

const logger = consola.withTag('AlfaBankWidget')

/** Как часто просим бэкенд сверить платёж напрямую со шлюзом. */
const SYNC_INTERVAL_MS = 30_000

const paymentStore = usePaymentStore()
const modalStore = useModalStore()
const { pollUntilPaid } = usePaymentReconciliation()

/**
 * Данные платежа приходят в модалку от того, кто её открыл (оформление заказа
 * или повтор оплаты). Запасной источник — payment store.
 *
 * Считаем их сразу в setup, чтобы первый же рендер был финальным: без ссылки на оплату
 * показывать пустую карточку и «моргать» ошибкой не нужно.
 */
const modalData = modalStore.modalData

const confirmationUrl = ref<string | null>(
  modalData?.confirmationUrl || paymentStore.confirmationUrl || null,
)
const isTest = ref(Boolean(modalData?.isTest))
/** mdOrder — идентификатор заказа в шлюзе; нужен для прямой сверки платежа. */
const paymentId = ref<string | null>(modalData?.paymentId || null)
const paymentError = ref<string | null>(
  confirmationUrl.value
    ? null
    : 'Не удалось получить ссылку для оплаты. Попробуйте оформить заказ заново.',
)
const isFrameLoading = ref(true)
const isAwaitingPayment = ref(true)

/** Параметры нативной кнопки Alfa Pay. null — кнопку не показываем. */
const alfaPayConfig = ref<AlfaPayNativeConfigResult | null>(null)
const isAlfaPayReady = computed(() => Boolean(alfaPayConfig.value?.token))

if (!confirmationUrl.value) {
  logger.error('Не пришла ссылка на оплату Альфа-Банка')
}

/**
 * Во фрейм платёжную страницу грузим только на сайте.
 *
 * В нативном приложении банк требует Chrome Custom Tabs / SFSafariViewController:
 * во встроенном WebView ломаются 3-D Secure и возврат из приложения банка.
 */
const useFrame = !isNativePlatform()

/** Остановка поллинга; обязательно дёргается на unmount. */
let stopPolling: (() => void) | null = null
let syncIntervalId: ReturnType<typeof setInterval> | null = null

// Модалку могут открыть и без payment store (например, повтор оплаты из списка заказов),
// поэтому id заказа сначала ищем в данных модалки.
const orderId = computed(() => modalData?.orderId ?? paymentStore.orderId)
const amountFormatted = computed(() => (paymentStore.amount ? String(paymentStore.amount) : ''))

const statusText = computed(() =>
  isAwaitingPayment.value ? 'Ждём подтверждения оплаты от банка…' : 'Оплата подтверждена',
)

function handleFrameLoad(): void {
  isFrameLoading.value = false
}

async function openPaymentPage(): Promise<void> {
  if (!confirmationUrl.value) return

  logger.debug('Открываем платёжную страницу Альфа-Банка', { orderId: orderId.value })
  await openExternalUrl(confirmationUrl.value)
}

/**
 * Спрашиваем бэкенд, доступна ли нативная кнопка Alfa Pay для этого заказа.
 *
 * Причин отказа хватает: у заведения не заведён открытый токен, включён тестовый режим
 * (у кнопки нет тестового контура) — тогда просто остаёмся на платёжной странице банка.
 */
async function loadAlfaPayConfig(): Promise<void> {
  if (!isAlfaPayNativeAvailable() || !orderId.value) return

  // Стор берём лениво: на сайте эта ветка не выполняется вовсе, а тянуть pinia
  // в setup ради неё незачем.
  const userId = useUserProfileStore().getUser?.id
  if (!userId) return

  try {
    const config = await apiAlfaBank.getAlfaPayConfig({
      orderId: Number(orderId.value),
      userId: String(userId),
    })

    if (config.available) {
      alfaPayConfig.value = config
    }
  } catch (error) {
    // Кнопка Alfa Pay — приятное дополнение, а не единственный путь: молча остаёмся
    // на обычной платёжной странице банка.
    logger.warn('Не удалось получить конфигурацию Alfa Pay', error)
  }
}

/**
 * Открывает нативный экран с кнопкой Alfa Pay.
 *
 * Успех здесь означает только «клиент ушёл платить»: оплату подтверждает callback банка,
 * а мы её видим обычным поллингом статуса заказа.
 */
async function openAlfaPay(): Promise<void> {
  const config = alfaPayConfig.value
  if (!config?.token) return

  try {
    const result = await AlfaPayNative.present({
      token: config.token,
      amount: config.amount!,
      orderNumber: config.orderNumber!,
      returnUrl: config.returnUrl!,
      failUrl: config.failUrl!,
      domain: config.domain!,
      description: config.description,
      email: config.email,
      phone: config.phone,
      clientId: config.clientId,
    })

    logger.debug('Нативный экран Alfa Pay закрыт', result)

    if (result.status === 'failed') {
      paymentError.value = result.message || 'Не удалось начать оплату через Alfa Pay'
    }
  } catch (error) {
    logger.error('Нативная кнопка Alfa Pay не открылась', error)
    paymentError.value = 'Не удалось открыть Alfa Pay. Попробуйте другой способ оплаты.'
  }
}

function stopWatchers(): void {
  stopPolling?.()
  stopPolling = null

  if (syncIntervalId != null) {
    clearInterval(syncIntervalId)
    syncIntervalId = null
  }
}

function handlePaid(): void {
  isAwaitingPayment.value = false
  stopWatchers()
  modalStore.close()
  modalStore.open(modalStateEnum.ModalThankYou)
}

/**
 * Просим бэкенд сверить платёж напрямую со шлюзом.
 *
 * Страховка на случай потерянного callback'а: банк повторяет уведомление всего 3 раза
 * с интервалом 30 секунд. Если оплата достроится, её увидит обычный поллинг статуса заказа —
 * поэтому здесь ничего закрывать не нужно.
 */
async function syncWithGateway(): Promise<void> {
  if (!paymentId.value) return

  try {
    await apiAlfaBank.syncStatus(paymentId.value)
  } catch (error) {
    // Сверка — вспомогательный механизм: её сбой не должен ломать экран оплаты
    logger.warn('Сверка платежа со шлюзом не удалась', error)
  }
}

onMounted(() => {
  if (!confirmationUrl.value) return

  // В приложении сначала выясняем, доступна ли нативная кнопка Alfa Pay: если да —
  // не уводим клиента в браузер автоматически, пусть выберет способ сам.
  //
  // Когда плагина в сборке нет, ждать нечего: поведение прежнее — сразу открываем
  // платёжную страницу, лишний тап не нужен.
  if (!useFrame) {
    if (isAlfaPayNativeAvailable()) {
      void loadAlfaPayConfig().then(() => {
        if (!isAlfaPayReady.value) {
          void openPaymentPage()
        }
      })
    } else {
      void openPaymentPage()
    }
  }

  // Источник истины об оплате — статус заказа на бэке (его выставляет callback банка).
  // Возврата с платёжной страницы может не быть вовсе: клиент платит в соседней вкладке
  // или в приложении банка, поэтому просто опрашиваем статус заказа.
  stopPolling = pollUntilPaid(orderId.value, handlePaid)

  if (paymentId.value) {
    syncIntervalId = setInterval(() => void syncWithGateway(), SYNC_INTERVAL_MS)
  }
})

onBeforeUnmount(stopWatchers)
</script>

<style scoped lang="scss">
.alpha-bank-widget {
  @apply p-2.5 bg-white dark:bg-gray-900;

  &__container {
    @apply
      grid gap-4 content-start justify-items-stretch
      md:gap-5
      bg-sky-300 dark:bg-zinc-900
      rounded-2xl
      pb-4 px-5
      md:py-8 md:px-10
      max-md:min-h-[calc(100vh-20px)];
  }

  &__hint {
    @apply text-center text-sm text-gray-700 dark:text-gray-300 justify-self-center max-w-md;
  }

  &__pay {
    @apply
      inline-flex items-center justify-center
      w-full max-w-md justify-self-center
      px-6 py-3
      rounded-xl
      bg-red-600 hover:bg-red-700
      text-white font-semibold text-lg
      transition-colors;

    // Когда доступна кнопка Alfa Pay, переход на платёжную страницу — запасной путь
    &--secondary {
      @apply
        bg-transparent hover:bg-black/5
        dark:hover:bg-white/5
        text-gray-700 dark:text-gray-300
        font-medium text-base;
    }
  }

  &__test {
    @apply text-center text-xs text-amber-700 dark:text-amber-400;
  }

  &__status {
    @apply text-center text-sm text-gray-700 dark:text-gray-300;
  }
}
</style>
